import classNames from "classnames";
import { useState } from "react";
import { DashboardUtils } from "../../../dashboard_utils";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";

export interface ProQuantityModalProps {
	open: boolean;
	onClose: () => void;
	availableSlots: number; // Max 3 for bundles
	currentProCount: number;
	onConfirm: (count: number) => void;
	isLoading?: boolean;
}

export function ProQuantityModal(props: ProQuantityModalProps) {
	const { open, onClose, availableSlots, currentProCount, onConfirm, isLoading } = props;
	const [selectedCount, setSelectedCount] = useState<number>(1);

	if (!open) {
		return null;
	}

	// Calculate how many Pro licenses can be added (max is availableSlots)
	const maxAddable = Math.min(availableSlots, 3);
	const options = Array.from({ length: maxAddable }, (_, i) => i + 1);

	// Calculate final counts after upgrade
	const finalProCount = currentProCount + selectedCount;
	const finalPremiumCount = availableSlots - selectedCount;

	return (
		<DashboardUtils.SeshModal
			is_active={open}
			onClose={onClose}
			title="Select Pro License Count"
			subtitle="Choose how many of your Premium slots to upgrade to sesh Pro"
			content={
				<div className="mt-4 flex flex-col gap-6">
					{/* Info about bundle */}
					<div className="rounded-lg border border-slate-600 bg-slate-900/50 p-4">
						<div className="flex items-center gap-2 text-sm text-slate-300">
							<i className="fas fa-info-circle text-sesh-primary"></i>
							<span>
								Your Premium Bundle has{" "}
								<span className="font-semibold text-white">
									{availableSlots}
								</span>{" "}
								server slots. You can upgrade some or all of
								them to sesh Pro.
							</span>
						</div>
					</div>

					{/* Quantity selection */}
					<div className="flex flex-col gap-3">
						<label className="text-sm font-medium text-slate-200">
							Number of Pro licenses:
						</label>
						<div className="flex flex-wrap gap-2">
							{options.map((count) => (
								<button
									key={count}
									onClick={() => setSelectedCount(count)}
									className={classNames(
										"flex min-w-[4rem] flex-col items-center rounded-lg border-2 px-4 py-3 transition-all",
										{
											"border-sesh-primary bg-sesh-primary/10 text-white":
												selectedCount === count,
											"border-slate-600 text-slate-300 hover:border-slate-500":
												selectedCount !== count,
										}
									)}
								>
									<span className="text-2xl font-bold">
										{count}
									</span>
									<span className="text-xs">
										Pro{count > 1 ? " licenses" : " license"}
									</span>
								</button>
							))}
						</div>
					</div>

					{/* Preview of final state */}
					<div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4">
						<h4 className="mb-3 text-sm font-medium text-slate-200">
							After upgrade:
						</h4>
						<div className="flex flex-col gap-2">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<i className="fas fa-crown text-amber-300"></i>
									<span className="text-white">
										sesh Pro servers
									</span>
								</div>
								<span className="font-semibold text-amber-300">
									{finalProCount}
								</span>
							</div>
							{finalPremiumCount > 0 && (
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<i className="fas fa-gem text-sesh-primary"></i>
										<span className="text-white">
											Premium servers
										</span>
									</div>
									<span className="font-semibold text-sesh-primary">
										{finalPremiumCount}
									</span>
								</div>
							)}
						</div>
					</div>

					{/* Confirm button */}
					<SeshPrimaryButton
						onClick={() => onConfirm(selectedCount)}
						disabled={isLoading}
						is_loading={isLoading}
						className={classNames(
							"w-full justify-center font-semibold",
							isLoading
								? "opacity-50"
								: "bg-sesh-primary !text-base-dark hover:bg-cyan-300"
						)}
					>
						<i className="fas fa-arrow-right mr-2"></i>
						{isLoading
							? "Redirecting to checkout..."
							: `Continue with ${selectedCount} Pro License${selectedCount > 1 ? "s" : ""}`}
					</SeshPrimaryButton>
				</div>
			}
		/>
	);
}
